/**
 * Mídia — enviar em lote, reaproveitar e remover.
 *
 * É aqui que mora a metade de navegador da decisão sobre vídeo: **quem mede a
 * duração e as dimensões é esta tela**, não o servidor. O servidor recebe os
 * números junto com os bytes e confere só plausibilidade (ver o bloco VÍDEO em
 * src/media/ingest.ts).
 *
 * A alternativa era `ffprobe` na EC2 — dependência de sistema numa máquina com
 * 34 processos — ou um parser de MP4 em JS. Como o sistema não transcodifica,
 * medir serve só para validar antes de agendar, e o navegador entrega isso de
 * graça.
 */
import { Api, type MidiaGuardada } from "../api.js";
import { bytesLegivel, el, horario, limpar } from "../dom.js";
import { marcaAtual } from "../marca.js";
import { avisar, type Tela } from "../tela.js";

/**
 * Mede um vídeo carregando-o num elemento `video` fora da tela.
 *
 * `duration` vem em SEGUNDOS e a API espera milissegundos — a multiplicação
 * está aqui, num lugar só. Mandar segundos faria a API recusar como
 * "implausível", que é o guarda que existe justamente para este erro.
 */
export function medirVideo(
  arquivo: File
): Promise<{ durationMs: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const v = document.createElement("video");
    v.preload = "metadata";

    const limpa = (): void => {
      URL.revokeObjectURL(url);
      v.removeAttribute("src");
    };

    v.addEventListener("loadedmetadata", () => {
      const dados = {
        durationMs: Math.round(v.duration * 1000),
        width: v.videoWidth,
        height: v.videoHeight
      };
      limpa();
      if (!Number.isFinite(dados.durationMs) || dados.width === 0) {
        // Acontece com contêiner que o navegador abre mas não decodifica.
        // Falhar aqui é melhor que mandar zeros para a API.
        reject(
          new Error(
            "O navegador não conseguiu ler a duração ou as dimensões deste vídeo. " +
              "Ele é MP4 com H.264? Reexporte antes de subir."
          )
        );
        return;
      }
      resolve(dados);
    });

    v.addEventListener("error", () => {
      limpa();
      reject(
        new Error(
          "O navegador não conseguiu abrir este arquivo como vídeo. " +
            "Renomear a extensão não converte o formato."
        )
      );
    });

    v.src = url;
  });
}

export function telaMidia(): Tela {
  return {
    titulo: "Mídia",
    async montar(raiz) {
      // Lida UMA vez, na montagem: trocar de marca no topo remonta a tela, e
      // um upload em andamento termina na marca em que começou.
      const marca = marcaAtual();
      const galeria = el("div", { class: "galeria" });

      const recarregar = async (): Promise<void> => {
        limpar(galeria);
        const { media } = await Api.midias(marca.slug);
        if (media.length === 0) {
          galeria.appendChild(
            el("p", { class: "sub" }, `Nada enviado para ${marca.nome} ainda.`)
          );
          return;
        }
        for (const m of media) galeria.appendChild(item(m, raiz, recarregar));
      };

      raiz.appendChild(caixaDeEnvio(marca.slug, marca.nome, raiz, recarregar));
      raiz.appendChild(el("h3", {}, "Já enviadas"));
      raiz.appendChild(galeria);
      await recarregar();
    }
  };
}

// ---------------------------------------------------------------- envio

/** Uma linha de progresso por arquivo do lote. */
function linhaDeProgresso(nome: string): {
  linha: HTMLElement;
  diz: (texto: string, classe?: string) => void;
} {
  const estado = el("span", { class: "sub" }, "aguardando");
  const linha = el(
    "div",
    { class: "linha-envio" },
    el("span", { class: "nome" }, nome),
    estado
  );
  return {
    linha,
    diz(texto, classe = "sub") {
      estado.className = classe;
      estado.textContent = texto;
    }
  };
}

function caixaDeEnvio(
  brand: string,
  nomeDaMarca: string,
  raiz: HTMLElement,
  recarregar: () => Promise<void>
): HTMLElement {
  const entrada = el("input", {
    type: "file",
    accept: "image/*,video/mp4",
    // Vários de uma vez: subir arte de campanha é sempre em lote.
    multiple: true
  }) as HTMLInputElement;

  const progresso = el("div", { class: "envios" });
  const resumo = el("p", { class: "sub" });
  const botao = el("button", {}, "Enviar") as HTMLButtonElement;

  botao.addEventListener("click", () => {
    const arquivos = [...(entrada.files ?? [])];
    if (arquivos.length === 0) {
      resumo.className = "sub";
      resumo.textContent = "Escolha ao menos um arquivo.";
      return;
    }

    botao.disabled = true;
    limpar(progresso);
    resumo.textContent = "";

    const linhas = arquivos.map((a) => {
      const p = linhaDeProgresso(a.name);
      progresso.appendChild(p.linha);
      return p;
    });

    void (async () => {
      let enviados = 0;
      let repetidos = 0;
      let falhas = 0;

      // UM DE CADA VEZ, e não em paralelo.
      //
      // Vídeo chega a 200MB e o upload é bufferizado em memória dos dois
      // lados; disparar cinco juntos derrubaria a API pelo teto de memória do
      // PM2. Em série é mais lento e não derruba ninguém.
      for (const [i, arquivo] of arquivos.entries()) {
        const p = linhas[i]!;
        try {
          const ehVideo = arquivo.type.startsWith("video/");
          let medidas:
            { durationMs: number; width: number; height: number } | undefined;

          if (ehVideo) {
            p.diz("medindo no navegador…");
            medidas = await medirVideo(arquivo);
          }

          p.diz("enviando…");
          const r = await Api.enviarMidia(brand, arquivo, medidas);

          if (r.deduped) {
            repetidos++;
            p.diz(`já existia — reaproveitado #${r.mediaId}`, "sub");
          } else {
            enviados++;
            p.diz(`#${r.mediaId} · ${r.width}×${r.height}`, "saida ok");
          }
        } catch (err) {
          // Um arquivo ruim não aborta o lote: os outros seguem, e no fim o
          // resumo diz quantos falharam. Interromper obrigaria a refazer tudo.
          falhas++;
          p.diz(err instanceof Error ? err.message : String(err), "erro-linha");
        }
      }

      const partes: string[] = [];
      if (enviados) partes.push(`${enviados} enviada(s)`);
      if (repetidos) partes.push(`${repetidos} já existia(m)`);
      if (falhas) partes.push(`${falhas} falhou(aram)`);
      resumo.className = falhas > 0 ? "erro-linha" : "saida ok";
      resumo.textContent = partes.join(" · ");

      entrada.value = "";
      botao.disabled = false;
      await recarregar().catch((e: unknown) =>
        avisar(raiz, e, "Falhou ao listar")
      );
    })();
  });

  return el(
    "div",
    { class: "caixa" },
    el("h3", {}, `Enviar para ${nomeDaMarca}`),
    el(
      "p",
      { class: "sub" },
      "Pode escolher vários de uma vez. Imagem é convertida para JPEG no envio; " +
        "vídeo tem duração e dimensões medidas aqui no navegador e mandadas junto."
    ),
    entrada,
    botao,
    progresso,
    resumo
  );
}

// ---------------------------------------------------------------- item

function item(
  m: MidiaGuardada,
  raiz: HTMLElement,
  recarregar: () => Promise<void>
): HTMLElement {
  const dim = m.width && m.height ? `${m.width}×${m.height}` : "sem dimensões";
  const dur =
    m.durationMs !== null ? ` · ${(m.durationMs / 1000).toFixed(1)}s` : "";

  const remover = el(
    "button",
    { class: "perigo pequeno" },
    "Remover"
  ) as HTMLButtonElement;

  remover.addEventListener("click", () => {
    // O texto diz "remover da biblioteca", não "apagar", porque é isso que
    // acontece: a policy do S3 não tem DeleteObject, e o arquivo continua
    // alcançável pela URL pública. Prometer o contrário faria alguém confiar
    // nisso para tirar do ar arte de campanha não lançada.
    if (
      !confirm(
        `Remover a mídia #${m.mediaId} da biblioteca?\n\n` +
          `Ela some daqui e do seletor de Nova peça. Se alguma peça estiver ` +
          `usando, a remoção é recusada.`
      )
    )
      return;

    remover.disabled = true;
    void Api.removerMidia(m.mediaId)
      .then(async (r) => {
        if (!r.removidoDoBucket) {
          // A verdade, dita na hora e sem rodeio.
          alert(
            `Removida da biblioteca.\n\n` +
              `MAS o arquivo continua no bucket e acessível por quem tiver a URL:\n` +
              `${r.publicUrl}\n\n` +
              `A policy do projeto não concede s3:DeleteObject. Se essa arte ` +
              `não pode ficar pública, peça a permissão ao Bruno — o sistema ` +
              `passa a apagar de verdade sozinho.`
          );
        }
        await recarregar();
      })
      .catch((err: unknown) => {
        remover.disabled = false;
        avisar(raiz, err, "Não deu para remover");
      });
  });

  return el(
    "figure",
    { class: "midia" },
    m.kind === "video"
      ? el("video", { src: m.publicUrl, controls: true, preload: "metadata" })
      : el("img", { src: m.publicUrl, alt: "", loading: "lazy" }),
    el(
      "figcaption",
      {},
      el("strong", { class: "mono" }, `#${m.mediaId}`),
      el(
        "span",
        { class: "sub" },
        `${dim}${dur} · ${m.bytes === null ? "?" : bytesLegivel(m.bytes)}`
      ),
      el("span", { class: "sub" }, horario(m.createdAt)),
      remover
    )
  );
}
